import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { CentsCurrencyPipe } from '@common/centsCurrency.pipe';
import { UpgradeHivRnaDisplay } from '@enums/upgrade-hiv-rna-display';
import { UpgradePanelTemplateContext } from '@interfaces/upgrade-panel-template-context';
import { AnalyteCareLoadCartRequest } from '@models/analyte-care-load-cart-request';
import { Test } from '@models/test';
import { UpgradeResponse } from '@models/upgrade-response';
import { OrderService } from '@services/order.service';
import { StorageService } from '@services/storage.service';

@Component({
  selector: 'app-upgrade',
  templateUrl: './upgrade.component.html',
  styleUrls: ['./upgrade.component.scss'],
  imports: [NgIf, NgFor, NgTemplateOutlet, CentsCurrencyPipe],
})
export class UpgradeComponent implements OnInit {
  public upgrade: Test;
  public upgradeTotal: number;

  private storage = inject(StorageService);
  private order = inject(OrderService);

  private childTests = [
    'HIV Type 1',
    'HIV Type 2',
    'Herpes 1',
    'Herpes 2',
    'Hepatitis A',
    'Hepatitis B',
    'Hepatitis C',
    'Chlamydia',
    'Gonorrhea',
    'Syphilis',
  ];
  private thirteenExtraTests = ['Mycoplasma Genitalium', 'Trichomoniasis', 'Wellness Panel'];
  private thirteenChildTests = [...this.childTests, ...this.thirteenExtraTests];

  /**
   * Get the current panel template context based on upgrade type.
   */
  public get currentPanelContext(): UpgradePanelTemplateContext | null {
    switch (true) {
      case this.is10TestPanelWithHivRnaUpgrade() && this.hasHivRnaTest():
        return this.buildPanelTemplateContext('10 Test Panel', this.childTests, UpgradeHivRnaDisplay.TitleAndItem);
      case this.is13TestPanelUpgrade():
        return this.buildPanelTemplateContext('13 Test Panel', this.thirteenChildTests);
      case this.is10TestPanelUpgrade():
        return this.buildPanelTemplateContext('10 Test Panel', this.childTests);
      case this.is13TestPanelWithHivRnaUpgrade() && this.hasHivRnaTest():
        return this.buildPanelTemplateContext(
          '13 Test Panel',
          this.thirteenChildTests,
          UpgradeHivRnaDisplay.TitleAndItem,
        );
      case this.is13TestPanelWithHivRnaUpgrade() && !this.hasHivRnaTest() && !this.has13TestPanel():
        return this.buildPanelTemplateContext(
          '13 Test Panel',
          this.thirteenExtraTests,
          UpgradeHivRnaDisplay.TitleOnly,
          'Get the most comprehensive STD testing',
        );
      default:
        return null;
    }
  }

  /**
   * Get the current order total.
   */
  public get currentTotal(): number {
    const testsTotalAmount = this.order.getTestsTotal();

    return Math.max(0, testsTotalAmount - this.order.getTotalCoupons(testsTotalAmount));
  }

  /**
   * Get the upgrade request.
   */
  private get upgradeRequest(): AnalyteCareLoadCartRequest {
    return new AnalyteCareLoadCartRequest({
      tests: this.storage.tests,
      coupon: this.order.couponCode,
    });
  }

  /**
   * Initialize the component.
   */
  public ngOnInit(): void {
    this.storage.testsSubject.subscribe(() => this.getUpgrade(this.upgradeRequest));
  }

  /**
   * Upgrade the order.
   */
  public onUpgrade(): void {
    const upgrade = this.upgrade;
    this.upgrade = null;

    this.order.loadCart([upgrade]).subscribe();
  }

  /**
   * Check if HIV RNA Early Detection exists in the current tests.
   */
  public hasHivRnaTest(): boolean {
    return this.storage.tests.some((test: Test) => ['hiv-rna', 'hiv-rna-test'].includes(test.slug));
  }

  /**
   * Check if '13-test-panel' exists in the current tests.
   */
  public has13TestPanel(): boolean {
    return this.storage.tests.some((test: Test) => test.slug === '13-test-panel');
  }

  /**
   * Check if upgrade is 10 Test Panel with HIV RNA.
   */
  public is10TestPanelWithHivRnaUpgrade(): boolean {
    return ['10-test-panel-with-hiv-rna-early-detection', '10-test-panel-with-hiv-rna'].includes(this.upgrade?.slug);
  }

  /**
   * Check if upgrade is 13 Test Panel with HIV RNA.
   */
  public is13TestPanelWithHivRnaUpgrade(): boolean {
    return this.upgrade?.slug === '13-test-panel-with-hiv-rna';
  }

  /**
   * Check if upgrade is 13 Test Panel.
   */
  private is13TestPanelUpgrade(): boolean {
    return this.upgrade?.slug === '13-test-panel';
  }

  /**
   * Check if upgrade is 10 Test Panel.
   */
  private is10TestPanelUpgrade(): boolean {
    return ['10-test-panel', '10-test-std-panel'].includes(this.upgrade?.slug);
  }

  /**
   * Get available upgrade.
   */
  private getUpgrade(request: AnalyteCareLoadCartRequest): void {
    this.order.getUpgrade(request).subscribe({
      next: this.onSuccess.bind(this),
      error: this.onError.bind(this),
    });
  }

  /**
   * Handle successful response.
   */
  private onSuccess(response: UpgradeResponse): void {
    this.upgrade = response.upgrade;
    this.upgradeTotal = response.upgradeTotal;
  }

  /**
   * Handle error response.
   */
  private onError(): void {
    this.upgrade = null;
    this.upgradeTotal = 0;
  }

  /**
   * Create template context for panel display.
   *
   * @param {string} panelName the name of the test panel
   * @param {string[]} tests array of test names to display in the panel
   * @param {UpgradeHivRnaDisplay} hivRnaDisplay controls how HIV RNA Early Detection is displayed
   * @param {string} title custom title text
   */
  private buildPanelTemplateContext(
    panelName: string,
    tests: string[],
    hivRnaDisplay: UpgradeHivRnaDisplay = UpgradeHivRnaDisplay.None,
    title: string = 'Save by testing for common STDs',
  ): UpgradePanelTemplateContext {
    return {
      panelName,
      tests,
      showHivRnaTitle: hivRnaDisplay !== UpgradeHivRnaDisplay.None,
      showHivRnaAsItem: hivRnaDisplay === UpgradeHivRnaDisplay.TitleAndItem,
      title,
    };
  }
}
